import { useEffect, useRef } from 'react'
import { ChatMessage } from './ChatMessage'
import { ChatInput } from './ChatInput'
import { useChat } from '../../hooks/useChat'
import { Loader2 } from 'lucide-react'
import { useAuth } from 'react-oidc-context'

interface ChatContainerProps {
}

export function ChatContainer({ }: ChatContainerProps) {
  const auth = useAuth()
  const { messages, sendMessage, isStreaming, isInitialized, initializationError } = useChat()
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const hasInitialized = useRef(false)
  // removed unused selectedProfile

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Initialize conversation on first load
  useEffect(() => {
    if (
      isInitialized &&
      !hasInitialized.current &&
      messages.length === 0 &&
      auth?.user?.access_token
    ) {
      hasInitialized.current = true
    }
  }, [isInitialized, messages.length])

  const handleSendMessage = async (message: string, profile: string) => {
    if (!auth?.user?.access_token) return
    await sendMessage(message, profile)
  }

  if (initializationError) {
    return (
      <div className="flex-1 flex items-center justify-center message-list-container">
        <div className="text-center">
          <p className="text-red-400 mb-2">❌ {initializationError}</p>
          <p className="text-gray-400 text-sm">Please check your CloudFormation stack configuration</p>
        </div>
      </div>
    )
  }

  if (!isInitialized) {
    return (
      <div className="flex-1 flex items-center justify-center message-list-container">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-2" />
          <p className="text-gray-400">Initializing agent...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col min-w-0 overflow-hidden message-list-container">
      {/* Scrollable message history */}
      <div className="flex-1 overflow-y-auto px-4 py-6 min-h-0">
        <div className="mx-auto space-y-4">
          {messages.map((message, index) => {
            const isLastMessage = index === messages.length - 1
            const isStreamingMessage = isStreaming && isLastMessage && message.role === 'assistant'

            return (
              <ChatMessage
                key={`${message.timestamp}-${index}`}
                message={message}
                isStreaming={isStreamingMessage}
              />
            )
          })}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Fixed chat input at bottom */}
      <div className="flex-shrink-0">
        <ChatInput
          onSend={handleSendMessage}
          disabled={isStreaming}
        />
      </div>
    </div>
  )
}